import React from 'react'
import { Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { FormattedMessage } from 'react-intl'
import { ProductSearchGroup } from '@src/types/graphql-types'
import { SubstanceFieldsFragment } from '@src/queries/ProductSearchQuery.generated'
import {
  AliasType,
  determineQueryFocus,
  formatCasNumberSearchTerm,
  getValidSearchQuery,
  ProductSearchType,
  SearchFocusType,
} from '@utils/searchUtils'
import { buildProductAliasPayload } from '@src/utils/analytics/analyticsUtils'
import messages from '../messages'
import HandleMarkup from '@src/components/HandleMarkup'
import { SubstanceInfoSegment } from '@src/components/SubstanceDetailSegment'
import { substanceSimilarityPct } from '@src/components/SubstanceCard/SubstanceCard'
import { useRouter } from '@src/routes'
import { sendProductInfoInteractionEvent } from '@src/utils/analytics'

const useStyles = makeStyles()((theme) => ({
  root: {
    alignItems: 'baseline',
    margin: 0,
    marginTop: theme.spacing(3),
    display: 'flex',
    flexWrap: 'wrap',
    gridRowGap: theme.spacing(2),
    '& > *': {
      margin: 0,
      flex: '0 0 16.66%',
    },
    '& > *:nth-of-type(4n)': {
      flexBasis: '25%',
    },
  },
  MetaFormatting: {
    wordBreak: 'break-all',
    fontWeight: theme.typography.fontWeightMedium,
  },
}))

interface ResponsiveSubstanceMetadataProps {
  substance: SubstanceFieldsFragment
}

const ResponsiveSubstanceMetadata: React.FC<
  ResponsiveSubstanceMetadataProps
> = ({ substance }) => {
  const { casNumber, molecularWeight, aliases, __typename } = substance
  const ecNumber = aliases.find((alias) => alias.key === AliasType.EcNumber)
  const beilsteinRegistryNumber = aliases.find((alias) =>
    alias.key.includes(AliasType.BeilsteinRegistryNumber)
  )

  const { classes } = useStyles()
  const router = useRouter()
  const validSearchQuery = getValidSearchQuery({
    focus: router.query.focus,
  })
  const substanceSimilarityValue =
    validSearchQuery.focus === SearchFocusType.StructureSearch
      ? substanceSimilarityPct(substance.products)
      : null

  const shouldDisplayField =
    __typename?.toLowerCase() === ProductSearchGroup.Substance

  return (
    <dl className={classes.root}>
      {casNumber && (
        <>
          <dt>
            <Typography component="div" variant="caption">
              <FormattedMessage {...messages.CAS_NO} />
              {': '}
            </Typography>
          </dt>
          <dd>
            <SubstanceInfoSegment
              substanceId={substance.id}
              label={casNumber}
              searchValue={formatCasNumberSearchTerm(casNumber)}
              searchFocusType={determineQueryFocus(validSearchQuery.focus)}
              productSearchType={ProductSearchType.CasNumber}
              classes={classes.MetaFormatting}
              onClick={() =>
                sendProductInfoInteractionEvent(
                  buildProductAliasPayload({
                    action: 'cas number',
                    detail: casNumber,
                    focus: validSearchQuery.focus,
                  })
                )
              }
            />
          </dd>
        </>
      )}
      {shouldDisplayField && molecularWeight && (
        <>
          <dt>
            <Typography
              component="div"
              variant="body2"
              className={classes.MetaFormatting}
            >
              <FormattedMessage {...messages.MOLECULAR_WEIGHT} />
              {': '}
            </Typography>
          </dt>
          <dd>
            <HandleMarkup value={molecularWeight} />
          </dd>
        </>
      )}
      {ecNumber && (
        <>
          <dt>
            <Typography
              component="div"
              variant="body2"
              className={classes.MetaFormatting}
            >
              <FormattedMessage {...messages.EC_NO} />
              {': '}
            </Typography>
          </dt>
          <dd>
            <SubstanceInfoSegment
              substanceId={substance.id}
              searchValue={ecNumber.value}
              searchFocusType={determineQueryFocus(validSearchQuery.focus)}
              productSearchType={ProductSearchType.EgecNumber}
              classes={classes.MetaFormatting}
              onClick={() =>
                sendProductInfoInteractionEvent(
                  buildProductAliasPayload({
                    action: 'ec number',
                    detail: ecNumber.value,
                    focus: validSearchQuery.focus,
                  })
                )
              }
            />
          </dd>
        </>
      )}
      {beilsteinRegistryNumber && (
        <>
          <dt>
            <Typography
              component="div"
              variant="body2"
              className={classes.MetaFormatting}
            >
              <FormattedMessage {...messages.BEILSTEIN_NO} />
              {': '}
            </Typography>
          </dt>
          <dd>
            <div className={classes.MetaFormatting}>
              {beilsteinRegistryNumber.value}
            </div>
          </dd>
        </>
      )}
      {substanceSimilarityValue && (
        <>
          <dt>
            <Typography
              component="div"
              variant="body2"
              className={classes.MetaFormatting}
            >
              <FormattedMessage {...messages.SIMILARITY} />
              {': '}
            </Typography>
          </dt>
          <dd>
            <span>{substanceSimilarityValue}%</span>
          </dd>
        </>
      )}
    </dl>
  )
}

export default ResponsiveSubstanceMetadata
